/**
 * Session Manager for Dashboard App
 * 
 * This wraps the shared ActivityTracker from @airclou/auth
 * and provides Dashboard-specific configuration and integration.
 * 
 * Session Debug Panel:
 * - By default, shown in development and hidden in production
 * - To enable in production: localStorage.setItem('dev:showSessionDebug', 'true')
 * - To disable in development: localStorage.setItem('dev:showSessionDebug', 'false')
 * - To enable via env: VITE_SHOW_SESSION_DEBUG=true
 */

import { 
  ActivityTracker, 
  configureActivityTracker,
  type SessionConfig 
} from '@airclou/auth-react';

/**
 * Check if session debug panel should be shown
 * Currently disabled - always returns false
 */
export function shouldShowSessionDebugPanel(): boolean {
  return false;
}

export function getSessionConfig(): SessionConfig {
  return {
    inactivityTimeoutMinutes: 30,
    tokenRefreshBeforeExpiryMinutes: 5,
    checkIntervalSeconds: 1,
  };
}



/**
 * Create the activity tracker for Dashboard.
 *
 * Observation only. This used to wire a per-tab inactivity logout: a 30-minute
 * timer that cleared the shared `.airclou.com` cookie and signed the whole suite
 * out. Its clock could only see *this* document's events, so a tab left open would
 * fire while the user was working in another app — and an app with no tracker
 * expired nothing at all. Idle belongs to the person, not the tab.
 *
 * The clock now lives on the session in auth, and `AppAuthProvider` owns both the
 * input listeners and the renewal, so the token, logout and redirect wiring that
 * used to live here is gone rather than duplicated. What remains is a local view of
 * this tab's last input, for the debug panel.
 *
 * See apps/auth/docs/session-idle-expiry-plan.md.
 */
export function createActivityTracker(): ActivityTracker {
  return new ActivityTracker({ isDevelopment: import.meta.env.DEV });
}

/**
 * Singleton activity tracker instance
 */
export const activityTracker = createActivityTracker();

/**
 * Start session tracking with configuration based on environment
 */
export function startSessionTracking(): void {
  const config = getSessionConfig();
  const trackerConfig = configureActivityTracker(config);
  
  activityTracker.start(trackerConfig);
  
  console.log('✅ Session tracking started with config:', config);
}

/**
 * Stop session tracking
 */
export function stopSessionTracking(): void {
  activityTracker.stop();
  console.log('🛑 Session tracking stopped');
}

// Development helper - expose to window for debugging
if (typeof window !== 'undefined' && import.meta.env.DEV) {
  (window as any).activityTracker = activityTracker;
  console.log('🔧 Activity tracker exposed to window for debugging');
  console.log('   Try: activityTracker.getInactiveTime()');
}
